import { Component, Input, OnChanges, SimpleChanges, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormArray, ValidationErrors, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ClassesEntity } from '../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { Log } from '../../../../../../services/logger.service';

@Component({
  selector: 'app-schedule-settings',
  templateUrl: './schedule-settings.component.html',
  styleUrls: ['./schedule-settings.component.css'],
})
export class ScheduleSettingsComponent implements OnChanges {
  @Input() classEntity!: ClassesEntity;
  @Input() classId!: string;

  form!: FormGroup;
  isSaving = false;

  private readonly logger = Log.create(ScheduleSettingsComponent);

  constructor(
    private readonly fb: FormBuilder,
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnChanges(_: SimpleChanges): void {
    const ts = this.classEntity?.trainingSchedule as any;
    const rawRanges: any[] = ts?.dateRanges ?? ts?.DateRanges ?? [];
    // Always show at least one empty row so the form is never blank.
    const ranges = rawRanges.length > 0 ? rawRanges : [null];

    this.form = this.fb.group({
      title: [ts?.title ?? ts?.Title ?? '', Validators.required],
      dateRanges: this.fb.array(ranges.map((dr: any) => this.buildRangeGroup(dr))),
    });
  }

  get ranges(): FormArray {
    return this.form.get('dateRanges') as FormArray;
  }

  private buildRangeGroup(dr?: any): FormGroup {
    return this.fb.group(
      {
        sessionTitle: [dr?.sessionTitle ?? dr?.SessionTitle ?? ''],
        startDate: [
          dr?.startDate ?? dr?.StartDate
            ? this.parseAsLocalDate(dr.startDate ?? dr.StartDate)
            : null,
          Validators.required,
        ],
        endDate: [
          dr?.endDate ?? dr?.EndDate
            ? this.parseAsLocalDate(dr.endDate ?? dr.EndDate)
            : null,
          Validators.required,
        ],
        startTime: [dr?.startTime ?? dr?.StartTime ?? '09:00', Validators.required],
        endTime: [dr?.endTime ?? dr?.EndTime ?? '17:00', Validators.required],
      },
      { validators: [dateRangeGroupValidator] },
    );
  }

  /** Returns true when group i has a specific group-level error and any field has been touched. */
  rangeHasError(i: number, errorKey: string): boolean {
    const g = this.ranges.at(i) as FormGroup;
    if (!g.hasError(errorKey)) return false;
    return (
      g.get('startDate')?.touched ||
      g.get('endDate')?.touched ||
      g.get('startTime')?.touched ||
      g.get('endTime')?.touched
    ) ?? false;
  }

  addRange(): void {
    this.ranges.push(this.buildRangeGroup());
  }

  removeRange(i: number): void {
    if (this.ranges.length > 1) {
      this.ranges.removeAt(i);
    }
  }

  private toDateInput(d: Date): string {
    return d.toISOString().substring(0, 10);
  }

  /**
   * Parses a date string or ISO datetime and returns a Date at local noon,
   * avoiding timezone-shift issues for date-only scheduling values.
   */
  private parseAsLocalDate(s: string | Date): Date | null {
    if (!s) return null;
    const dateStr = s instanceof Date ? s.toISOString() : String(s);
    const datePart = dateStr.substring(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;
    const d = new Date(`${datePart}T12:00:00`);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Serialises a Date (or bare YYYY-MM-DD string) to a YYYY-MM-DD string
   * using LOCAL calendar date parts, so the backend receives the exact day
   * the user picked regardless of timezone.
   */
  private formatLocalDate(d: any): string | null {
    if (!d) return null;
    if (d instanceof Date) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    return String(d).substring(0, 10) || null;
  }

  save(): void {
    if (this.form.invalid) return;
    this.isSaving = true;
    const v = this.form.getRawValue();

    const request = {
      Title: v.title,
      DateRanges: v.dateRanges.map((r: any) => ({
        SessionTitle: r.sessionTitle || null,
        StartDate: this.formatLocalDate(r.startDate),
        EndDate: this.formatLocalDate(r.endDate),
        StartTime: r.startTime || null,
        EndTime: r.endTime || null,
      })),
    };

    this.classService.updateTrainingSchedule(this.classId, request).subscribe({
      next: () => {
        this.isSaving = false;
        this.snackBar.open('Schedule saved', 'Dismiss', { duration: 2500, panelClass: 'snack-success' });
        this.logger.i('Schedule saved');
      },
      error: (err: any) => {
        this.isSaving = false;
        this.logger.e('Save schedule error:', err);
        this.snackBar.open('Failed to save schedule', 'Dismiss', { duration: 3000, panelClass: 'snack-error' });
      },
    });
  }
}

/**
 * Cross-field validator for a single date-range group.
 *
 * Checks:
 * 1. `endBeforeStart`   — endDate is earlier than startDate.
 * 2. `endTimeBeforeStart` — startTime >= endTime on the SAME calendar day
 *    (cross-day sessions can legitimately have a later start-time than end-time).
 */
function dateRangeGroupValidator(g: AbstractControl): ValidationErrors | null {
  const startDate: Date | null = g.get('startDate')?.value ?? null;
  const endDate: Date | null   = g.get('endDate')?.value   ?? null;
  const startTime: string      = g.get('startTime')?.value ?? '';
  const endTime: string        = g.get('endTime')?.value   ?? '';

  const errors: ValidationErrors = {};

  if (startDate instanceof Date && endDate instanceof Date) {
    const startDay = toDateKey(startDate);
    const endDay   = toDateKey(endDate);

    if (endDay < startDay) {
      errors['endBeforeStart'] = true;
    }

    // Only enforce time ordering when both times are entered and it is a same-day range.
    if (startTime && endTime && startDay === endDay && startTime >= endTime) {
      errors['endTimeBeforeStart'] = true;
    }
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

/** Returns a sortable YYYY-MM-DD string from a local Date (avoids UTC shift). */
function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
